import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { ThemeStateService } from '@sites/site-theme/src/state';
import { CSS_RESTRICTIONS } from '@common/constants';
import type { GallerySettingsFormGroup } from '@web/widgets/catalog-products/type';
import { AspectRatio, type GallerySettingsFormType } from '@web/widgets/catalog-products/type';
import { Attachment } from '@web/widgets/catalog-products/type/catalog-products-widget.type';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CommonModule } from '@angular/common';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

@Component({
    selector: 'web-gallery-settings',
    templateUrl: './gallery-settings.component.html',
    styleUrls: ['./gallery-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CommonModule, CommonElementsModule],
})
export class GallerySettingsComponent {
    @Input() gallerySettingsFormGroup: GallerySettingsFormGroup;
    @Input() widgetId;

    private readonly themeState = inject(ThemeStateService);

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public aspectRatioOptions = Object.values(AspectRatio);
    public attachmentOptions = Object.values(Attachment);

    public setMargin(value: number): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.margin.patchValue(value);
    }

    public setBorderRadius(value: number): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.borderRadius.patchValue(value);
    }

    public setRatio(event) {
        const swiperWrapper = document.querySelector(`[id='${this.widgetId}'] .swiper-wrapper`) as HTMLDivElement;

        if (swiperWrapper) {
            if (event.target.value !== AspectRatio.default) {
                swiperWrapper.style.removeProperty('height');
            } else {
                setTimeout(() => {
                    swiperWrapper.style.height = 'auto';
                }, 100);
            }
        }
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.gallerySettingsFormGroup.markAsTouched({ onlySelf: true });

        this.backgroundColor.patchValue(presetName ?? value);
    }

    public get aspectRatio(): GallerySettingsFormType['aspectRatio'] {
        return this.gallerySettingsFormGroup.get('aspectRatio') as GallerySettingsFormType['aspectRatio'];
    }

    public get attachment(): GallerySettingsFormType['attachment'] {
        return this.gallerySettingsFormGroup.get('attachment') as GallerySettingsFormType['attachment'];
    }

    public get backgroundColor(): GallerySettingsFormType['backgroundColor'] {
        return this.gallerySettingsFormGroup.get('backgroundColor') as GallerySettingsFormType['backgroundColor'];
    }

    public get borderRadius(): GallerySettingsFormType['borderRadius'] {
        return this.gallerySettingsFormGroup.get('borderRadius') as GallerySettingsFormType['borderRadius'];
    }

    public get margin(): GallerySettingsFormType['margin'] {
        return this.gallerySettingsFormGroup.get('margin') as GallerySettingsFormType['margin'];
    }

    public get BORDER_RADIUS_MIN(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    }

    public get BORDER_RADIUS_MAX(): number {
        return CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    }

    public get MARGIN_MIN(): number {
        return CSS_RESTRICTIONS.MARGIN_MIN;
    }

    public get MARGIN_MAX(): number {
        return CSS_RESTRICTIONS.MARGIN_MAX;
    }
}
